import { Col, Form, message, Row } from "antd";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { StoreConfigForm } from "forms/StoreConfigForm";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getStoreConfig, updateStoreConfig } from "store/ppd/ppdSlice";

const MESSAGE_KEY = "edit-store-config";

const EditStoreConfigPage = () => {
  const [form] = Form.useForm();
  const { storeConfig, status, error } = useAppSelector((state) => state.ppd);
  const { storeId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onCancel = useCallback(() => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(`/${routes.PPD.STORE_CONFIGS}`);
    }
  }, [navigate]);

  useEffect(() => {
    if (storeId) {
      dispatch(getStoreConfig(storeId));
    } else {
      navigate(routes.PPD.STORE_CONFIGS);
    }
  }, [storeId, dispatch, navigate]);

  useEffect(() => {
    if (status === "saving") {
      message.loading({ content: "Saving store configuration...", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({ content: "Store configuration saved successfully!", key: MESSAGE_KEY, duration: 2 });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY, duration: 3 });
    }

    return () => message.destroy();
  }, [status, error]);

  const onSubmit = () => {
    form.submit();
  };

  const onFinish = (values: Record<string, any>) => {
    const formValues = {
      ...storeConfig,
      ...values,
      storeId: parseInt(storeId!),
      // Format time values to HH:mm:ss string
      pickPackStartTime: values.pickPackStartTime?.format("HH:mm:ss"),
      dispatchStartTime: values.dispatchStartTime?.format("HH:mm:ss"),
    };

    dispatch(updateStoreConfig(formValues as any))
      .unwrap()
      .then(() => {
        setTimeout(() => {
          navigate(`/${routes.PPD.STORE_CONFIGS}`);
        }, 1000);
      })
      .catch((err) => {
        console.error("Failed to update store config:", err);
      });
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Edit Store Configuration" />
        </Col>
      </Row>
      <Row>
        <Col xs={24} lg={18} xl={16}>
          {storeConfig && (
            <StoreConfigForm
              formInstance={form}
              values={storeConfig}
              onCancel={onCancel}
              onFinish={onFinish}
              onSubmit={onSubmit}
              loading={status === "saving"}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default EditStoreConfigPage;
